<script setup lang="ts">
import { computed } from 'vue';

import { formatMoney, useConfirm } from '@/shared/lib';
import { AppButton, AppSpinner } from '@/shared/ui';

import type { OrderFiscalizationPhase } from '../model/order-fiscalization-state';

import { BUSY_PHASES } from '../model/order-fiscalization-state';

const props = defineProps<{
  amount: number;
  canCancelPayment?: boolean;
  canRetryReceipt?: boolean;
  canResolveReceipt?: boolean;
  receiptQuestion?: boolean;
  cashToReturn?: number;
  cancellable?: boolean;
  message?: string | null;
  operation?: 'refund' | 'order';
  phase: OrderFiscalizationPhase;
  shiftAction?: 'open' | 'reopen' | null;
  terminalName?: string | null;
  technicalDetails?: string | null;
}>();

const emit = defineEmits<{
  cancel: [];
  cancelPayment: [];
  cancelWithoutCheck: [];
  retryFinalize: [];
  retryReceipt: [];
  resolveReceipt: [];
  answerReceiptPrinted: [printed: boolean];
  fixShift: [];
}>();

const { confirm } = useConfirm();

const title = computed(() => {
  // Итог отмены и есть заголовок: «Деньги возвращены клиенту», «Списания не было».
  if (props.phase === 'payment_cancelled') return props.message ?? 'Платёж отменён';
  if (props.operation === 'refund') {
    const refundTitles: Partial<Record<OrderFiscalizationPhase, string>> = {
      checking_readiness: 'Проверяем кассу',
      preparing: 'Готовим возврат',
      waiting_payment: 'Ждём возврат на терминале',
      printing: 'Печатаем чек возврата',
      finalizing: 'Завершаем возврат',
      payment_declined: 'Банк отклонил возврат',
      cancelled_on_terminal: 'Возврат отменён на терминале',
      failed_before_print: 'Возврат не начался',
      print_result_unknown: 'Непонятно, вышел ли чек возврата',
      payment_without_receipt: 'Деньги возвращены, чека нет',
      printed_pending_sync: 'Чек напечатан, возврат не сохранён',
      checking_receipt: 'Проверяем чек в кассе',
      kkt_unavailable: 'Касса не отвечает',
    };

    return refundTitles[props.phase] ?? 'Касса';
  }

  const titles: Partial<Record<OrderFiscalizationPhase, string>> = {
    checking_readiness: 'Проверяем кассу',
    preparing: 'Готовим чек',
    waiting_payment: 'Ждём оплату на терминале',
    checking_payment_result: 'Проверяем результат оплаты',
    printing: 'Печатаем чек',
    finalizing: 'Завершаем оплату',
    payment_declined: 'Банк отклонил оплату',
    cancelled_on_terminal: 'Оплата отменена на терминале',
    payment_unconfirmed: 'Оплата картой не подтвердилась',
    cancelling_payment: 'Отменяем платёж',
    order_cancelled: 'Оплата отменена',
    failed_before_print: 'Оплата не началась',
    print_result_unknown: 'Непонятно, вышел ли чек',
    payment_without_receipt: 'Деньги списаны, чека нет',
    printed_pending_sync: 'Чек напечатан, оплата не сохранена',
    checking_receipt: 'Проверяем чек в кассе',
    kkt_unavailable: 'Касса не отвечает',
  };
  if (props.canRetryReceipt) return 'Закончилась бумага, оплата получена';
  return titles[props.phase] ?? 'Касса';
});

/** Операция не прошла или ждёт решения кассира: красный заголовок отличает ошибку от хода операции. */
const PROBLEM_PHASES: ReadonlySet<OrderFiscalizationPhase> = new Set([
  'payment_declined',
  'cancelled_on_terminal',
  'payment_unconfirmed',
  'failed_before_print',
  'print_result_unknown',
  'payment_without_receipt',
  'printed_pending_sync',
  'kkt_unavailable',
]);

const active = computed(() => BUSY_PHASES.has(props.phase));

async function cancelWithoutCheck(): Promise<void> {
  const confirmed = await confirm({
    title: 'Отменить платёж без проверки?',
    message:
      'Терминал не проверит, списались ли деньги. Если клиент вернётся со списанием, деньги можно будет вернуть на карту.',
    confirmText: 'Отменить платёж',
  });

  if (confirmed) emit('cancelWithoutCheck');
}
</script>

<template>
  <section
    class="flex min-h-64 flex-col items-center justify-center rounded-2xl border border-(--border) bg-(--surface-sunken) p-6 text-center"
    aria-live="assertive"
  >
    <AppSpinner
      v-if="active"
      class="mb-4"
    />
    <p class="text-xs font-semibold tracking-[0.14em] text-(--text-secondary) uppercase">
      {{ terminalName ?? 'Касса' }}
    </p>
    <h3
      class="mt-2 text-lg font-semibold text-balance"
      :class="PROBLEM_PHASES.has(phase) ? 'text-(--danger)' : 'text-(--text-primary)'"
    >
      {{ title }}
    </h3>
    <strong class="mt-3 text-3xl text-(--text-primary) tabular-nums">{{ formatMoney(amount) }}</strong>
    <p
      v-if="phase === 'payment_unconfirmed'"
      class="mt-3 max-w-sm text-sm leading-5 text-pretty text-(--text-secondary)"
    >
      Неизвестно, списались ли деньги. Отмените платёж, чтобы принять оплату заново.
    </p>
    <template v-if="phase === 'cancelling_payment'">
      <p
        class="mt-4 max-w-sm rounded-xl border border-(--accent)/40 bg-(--accent)/15 px-4 py-3 text-base leading-6 font-semibold text-pretty text-(--text-primary)"
      >
        Попросите клиента приложить карту или телефон к терминалу
      </p>
      <!-- Из браузера команду терминала не прервать (`cancelActiveCommand: null`): выход только на терминале. -->
      <p class="mt-3 max-w-sm text-sm leading-5 text-pretty text-(--text-secondary)">
        Если не выходит, нажмите «Отмена» на терминале.
      </p>
    </template>
    <p
      v-if="phase === 'payment_cancelled' && cashToReturn"
      class="mt-4 max-w-sm rounded-xl border border-(--accent)/40 bg-(--accent)/15 px-4 py-3 text-base leading-6 font-semibold text-pretty text-(--text-primary)"
    >
      Верните клиенту {{ formatMoney(cashToReturn) }} наличными
    </p>
    <p
      v-if="message && phase !== 'payment_cancelled'"
      class="mt-3 max-w-sm text-sm leading-5 text-pretty"
      :class="phase === 'payment_unconfirmed' ? 'text-(--danger)' : 'text-(--text-secondary)'"
    >
      {{ message }}
    </p>
    <AppButton
      v-if="canRetryReceipt"
      class="mt-5 min-h-11"
      :disabled="active"
      @click="emit('retryReceipt')"
    >
      Повторить печать
    </AppButton>
    <AppButton
      v-if="canResolveReceipt"
      class="mt-5 min-h-11"
      :disabled="active"
      @click="emit('resolveReceipt')"
    >
      {{ phase === 'kkt_unavailable' ? 'Проверить ещё раз' : 'Напечатать чек' }}
    </AppButton>
    <div
      v-if="receiptQuestion"
      class="mt-5 flex flex-wrap justify-center gap-3"
    >
      <AppButton
        class="min-h-11"
        :disabled="active"
        @click="emit('answerReceiptPrinted', true)"
      >
        Чек вышел
      </AppButton>
      <AppButton
        class="min-h-11"
        outline
        :disabled="active"
        @click="emit('answerReceiptPrinted', false)"
      >
        Чек не вышел
      </AppButton>
    </div>
    <template v-if="canCancelPayment">
      <AppButton
        class="mt-5 min-h-11"
        :outline="canRetryReceipt"
        @click="emit('cancelPayment')"
      >
        Отменить платёж
      </AppButton>
      <p class="mt-2 max-w-sm text-xs leading-5 text-pretty text-(--text-secondary)">
        {{
          canRetryReceipt
            ? 'Если чек напечатать не выйдет: клиент приложит карту или телефон, деньги вернутся.'
            : 'Клиент ещё раз приложит карту или телефон. Если деньги списались, они вернутся.'
        }}
      </p>
    </template>
    <button
      v-if="phase === 'payment_unconfirmed'"
      type="button"
      class="mt-3 inline-flex min-h-11 items-center text-sm font-medium text-(--accent) underline"
      @click="cancelWithoutCheck"
    >
      Карту приложить нельзя или клиент платил по СБП
    </button>
    <details
      v-if="technicalDetails && !active"
      class="mt-4 w-full max-w-sm text-left text-xs leading-5 text-(--text-secondary)"
    >
      <summary class="flex min-h-11 cursor-pointer items-center justify-center">Технические подробности</summary>
      <p class="mt-2 wrap-anywhere whitespace-pre-wrap">{{ technicalDetails }}</p>
    </details>
    <AppButton
      v-if="phase === 'waiting_payment' && cancellable"
      class="mt-5 min-h-11"
      outline
      @click="emit('cancel')"
    >
      Отменить оплату
    </AppButton>
    <AppButton
      v-if="shiftAction"
      class="mt-5 min-h-11"
      @click="emit('fixShift')"
    >
      {{ shiftAction === 'reopen' ? 'Закрыть и открыть новую' : 'Открыть смену' }}
    </AppButton>
    <AppButton
      v-if="phase === 'printed_pending_sync'"
      class="mt-5 min-h-11"
      @click="emit('retryFinalize')"
    >
      {{ operation === 'refund' ? 'Сохранить возврат' : 'Сохранить оплату' }}
    </AppButton>
  </section>
</template>
